import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import mixpanel from "mixpanel-browser";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, IntegrationTypeEnum, ThemeTypeEnum } from "~/types/enums";
import { Alert, Box, Button, Card, Grid, Icon, InputCheckbox, InputText, InputToggle, Link, Picture, Spinner, Text } from "~/components-shared";
import integrationsActionCreators from "~/store/integrations/actions";
import integrationSelectors from "~/store/integrations/selectors";
import githubDark from "~/assets/i/apps/github_logo_dark.svg";
import githubLight from "~/assets/i/apps/github_logo_white.svg";
import useAbacExt from "~/hooks/abac";
import api from "~/lib/api";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { handleError, helpers } from "~/lib/helpers";
const Actions = styled.div`
  position: relative;
  z-index: 2;
`;
const StyledGrid = styled(Grid)`
  position: relative;
  z-index: 2;
  max-height: 300px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;
const GithubIntegrationCard = _ref => {
  let {
    ...rest
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    userHasOneOf
  } = useAbacExt();
  const {
    type
  } = useTheme();
  const dispatch = useDispatch();
  const integration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.GITHUB));
  const [githubRepositories, setGithubRepositories] = useState([]);
  const [loadingGithubRepos, setLoadingGithubRepos] = useState(false);
  const [githubReposError, setGithubReposError] = useState();
  const [isExpanded, setIsExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const handleLoadGithubRepositories = () => {
    setLoadingGithubRepos(true);
    setGithubReposError(undefined);
    api.fetchGithubRepositories().then(data => setGithubRepositories(data)).catch(e => setGithubReposError(handleError(e))).finally(() => setLoadingGithubRepos(false));
  };
  useEffect(() => {
    if (integration && userHasOneOf([AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE])) {
      handleLoadGithubRepositories();
    }
  }, [!!integration]);
  const handleToggle = value => {
    if (value) {
      api.githubInstall().then(_ref2 => {
        let {
          path
        } = _ref2;
        const width = 1000;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        window.open(path, "Install Github", `width=${width},height=${height},left=${left},top=${top}`);
      });
      setIsExpanded(true);
      mixpanel.track(`Github Integration – Install`);
    } else if (integration?.id) {
      dispatch(integrationsActionCreators.deleteStarted(integration.id));
      mixpanel.track(`Github Integration – Uninstall`);
    }
  };
  const handleUpdate = function (repositoryIds) {
    let force = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    let deselect = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (integration) {
      let repositories = integration.settings?.repositories || [];
      repositoryIds.forEach(repositoryId => {
        if (repositories.includes(repositoryId) && !force || deselect && force) {
          repositories = repositories.filter(id => id !== repositoryId);
        } else {
          repositories.push(repositoryId);
        }
      });
      dispatch(integrationsActionCreators.updateStarted(integration.id, {
        type: integration.type,
        token: integration.token,
        settings: {
          ...integration.settings,
          repositories
        }
      }));
      mixpanel.track(`Github Integration – Update`, {
        ...helpers.objectDiff({
          ...integration.settings
        }, {
          ...integration.settings,
          repositories
        })
      });
    }
  };
  const handleSearch = value => {
    setSearch(value);
  };
  const filteredGithubRepositories = githubRepositories.filter(repo => repo.name.toLowerCase().includes(search.toLowerCase()) || repo.description && repo.description.toLowerCase().includes(search.toLowerCase()) || repo.fullName.toLowerCase().includes(search.toLowerCase()));
  const hasFiltered = filteredGithubRepositories.length && search;
  return /*#__PURE__*/React.createElement(Card, _extends({
    onClick: userHasOneOf([AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE]) && integration ? () => setIsExpanded(prev => !prev) : undefined,
    elemBefore: /*#__PURE__*/React.createElement(Picture, {
      src: type === ThemeTypeEnum.DARK ? githubLight : githubDark,
      width: "30px",
      height: "30px",
      border: "squared",
      color: "transparent",
      s: {
        mr: 2
      }
    }),
    title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      display: "inline-block"
    }, "Github"), integration && userHasOneOf([AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE]) && /*#__PURE__*/React.createElement(Icon, {
      scale: 4,
      src: "chevron",
      rotate: isExpanded ? 0 : 180,
      s: {
        ml: 1,
        mb: 1
      }
    })),
    elemAfter: /*#__PURE__*/React.createElement(Actions, null, /*#__PURE__*/React.createElement(InputToggle, {
      display: "inline-block",
      value: !!integration,
      onChange: (value, event) => {
        event.stopPropagation();
        handleToggle(value);
      },
      disabled: !userHasOneOf([AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE])
    })),
    collapsed: !integration || !isExpanded,
    s: {
      mb: 3
    }
  }, rest), githubReposError && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, githubReposError), integration?.settings && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    display: "block",
    s: {
      mb: 3
    }
  }, t("CompanyIntegrations.connectedTo"), " ", /*#__PURE__*/React.createElement(Link, {
    href: integration.settings.orgUrl,
    underline: true
  }, integration.settings.orgName), " ", t("CompanyIntegrations.organization"), " with the following repositories selected:"), /*#__PURE__*/React.createElement(InputText, {
    onClick: e => e.stopPropagation(),
    s: {
      mb: 4
    },
    value: search,
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "search",
      s: {
        mr: 2
      }
    }),
    placeholder: t("common.actions.search"),
    onChange: val => handleSearch(val),
    elemAfter: /*#__PURE__*/React.createElement(Box, {
      gap: 1,
      s: {
        ml: 1
      }
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: e => {
        e.stopPropagation();
        handleUpdate(filteredGithubRepositories.map(r => r.id), true);
      },
      color: "translucent",
      outline: false,
      fontSize: 3,
      s: {
        px: 3,
        py: 1
      },
      rounded: true
    }, hasFiltered ? "Select found" : "Select all"), /*#__PURE__*/React.createElement(Button, {
      onClick: e => {
        e.stopPropagation();
        handleUpdate(filteredGithubRepositories.map(r => r.id), true, true);
      },
      color: "translucent",
      fontSize: 3,
      outline: false,
      s: {
        px: 3,
        py: 1
      },
      rounded: true
    }, hasFiltered ? "Deselect found" : "Deselect all")),
    clearButton: true,
    fullWidth: true
  }), loadingGithubRepos ? /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }) : filteredGithubRepositories.length ? /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 2,
    template: "1rem 1fr",
    s: {
      pl: 2
    },
    gap: 3,
    justify: "center"
  }, filteredGithubRepositories.map(repository => /*#__PURE__*/React.createElement(React.Fragment, {
    key: repository.id
  }, /*#__PURE__*/React.createElement(InputCheckbox, {
    value: integration?.settings?.repositories?.includes(repository.id),
    onChange: () => handleUpdate([repository.id]),
    s: {
      mt: 1
    }
  }), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "semiBold"
  }, repository.name), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "semiBold",
    color: "textSecondary"
  }, repository.description || "No description"))))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "semiBold",
    s: {
      my: 3
    }
  }, "No Repositories found")));
};
export default GithubIntegrationCard;